import {Injectable, inject} from '@angular/core';
import {DOCUMENT} from '@angular/common';
import {
  ApplicationInsights,
  ICustomProperties,
  IExceptionTelemetry,
  ITelemetryItem
} from '@microsoft/applicationinsights-web';
import {environment} from '../../environments/environment';
import {EventsManager} from '../events/events.manager';
import {PLACEHOLDER_ORGANIZATION_ID} from '../core/constants/organization.constants';

@Injectable({providedIn: 'root'})

export class AppInsightsService {
  private _eventsManager: EventsManager = inject(EventsManager);
  private _document: Document = inject(DOCUMENT);

  private _appInsights: ApplicationInsights;

  constructor() {
    this._appInsights = new ApplicationInsights({
      config: {
        instrumentationKey: environment.appInsights.instrumentationKey,
        enableAutoRouteTracking: true
      }
    });

    this._appInsights.loadAppInsights();
    this._registerOriginAndOrganizationTelemetryInitializer();
  }

  private _registerOriginAndOrganizationTelemetryInitializer(): void {
    this._appInsights.addTelemetryInitializer((envelope: ITelemetryItem): void => {
      envelope.data = envelope.data ?? {};
      const origin: string | undefined = this._document.location?.origin;
      if (origin) {
        envelope.data['origin'] = origin;
      }
      const organizationId: string = this._eventsManager.orgId;
      if (organizationId && organizationId !== PLACEHOLDER_ORGANIZATION_ID) {
        envelope.data['organizationId'] = organizationId;
      }
    });
  }

  public logError(error: IExceptionTelemetry, ...rest: ICustomProperties[]): void {
    environment.production ? this._appInsights.trackException(error, rest) : console.error(error, ...rest);
  }

  public trackEvent(name: string, properties?: Record<string, unknown>): void {
    try {
      if (environment.production) {
        this._appInsights.trackEvent({name}, properties as ICustomProperties);
      } else {
        console.debug(`[Smile Health][telemetry] ${name}`, properties ?? {});
      }
    } catch {
      // Telemetry must never break the render pipeline (ad-blocker / CSP / SDK failure).
    }
  }

  public logTimeStamp(label: string, type: string = '', end: boolean = false): void {
    environment.production
      ? this._appInsights.trackTrace({message: label}, {type: type, end: end})
      : (() => {
        const logLabel = `[Smile Health] ${type ? type + ' ' : ''}${label}`;
        if (end) {
          console.timeEnd(logLabel);
        } else {
          console.time(logLabel);
        }
        console.timeStamp();
      })();
  }
}
